import { Box } from "@mui/material";
import { useState, useEffect } from "react";
import { ShippingGroupPreview } from "../ShippingGroupPreview";

import { useCheckoutContext } from "../../../CheckoutMachine";

import { ChevronDownIcon, MOBILE_SCREEN_SIZE } from "@brandclub/common-ui";
import { KeyboardArrowRightRounded } from "@mui/icons-material";
import {
  AsyncCheckoutOrderType,
  ShippingGroup,
  ShoppingCartItem,
  UserApprovalStatus,
  UserInteractionType,
  UserRetailerCheckoutOrderStatusV2,
} from "../../../types";
import { SummaryProduct } from "../../SummaryProductV2";
import { useSelector } from "@xstate/react";
import { StoreErrorV2 } from "@/components/StoreComponents/StoreError";
import ButtonContainer from "../components/Buttons/ButtonContainer";
import SaveButton from "../components/Buttons/SaveButton";
import moment from "moment";
import { ProductReturnDialog } from "@/components/StoreComponents/StoreDialogs/ProductReturnDialog";
import pluralize from "pluralize";
import { useStorefrontReturnsEnabled } from "./useStorefrontReturnsEnabled";

export const getErrorProducts = (items: ShoppingCartItem[]) => {
  let res: ShoppingCartItem[] = items;
  res = res.filter(
    (item) => item.status === "error" && item?.userApprovalStatus !== "deleted"
  );
  return res;
};

const ErrorProducts = ({
  errorProducts,
}: {
  errorProducts: ShoppingCartItem[];
}) => {
  const { send, actorRef } = useCheckoutContext();
  const [outOfStockProduct, setOutOfStockProduct] =
    useState<ShoppingCartItem[]>(errorProducts);

  const onRemove = (sku: string) => {
    setOutOfStockProduct(
      outOfStockProduct.filter((e) => e.stacklineSku !== sku)
    );
    if (outOfStockProduct.filter((e) => e.stacklineSku !== sku).length === 0) {
      // removed all, now should call confirm to continue order
      send({
        type: "SUBMIT_USER_INTERACTION",
        data: {
          interactionType: UserInteractionType.INVENTORY_CONFIRMATION,
          interactionData: {
            approve: true,
          },
        },
      });
    }
  };

  const retailerCheckoutOrderStatus = useSelector(
    actorRef,
    (snapshot) => snapshot.context.retailerCheckoutOrderStatus
  );

  const userInteractionRequirement =
    retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
      ?.userInteractionRequirement;
  const disabled =
    userInteractionRequirement?.type ===
    UserInteractionType.NO_INVENTORY_AVAILABLE;

  const isActionable =
    retailerCheckoutOrderStatus?.userRetailerCheckoutOrder?.status ===
      UserRetailerCheckoutOrderStatusV2.queued ||
    retailerCheckoutOrderStatus?.userRetailerCheckoutOrder?.status ===
      UserRetailerCheckoutOrderStatusV2.checkout_in_progress;

  return (
    <>
      {outOfStockProduct.map((errorProduct) => {
        return (
          <SummaryProduct
            product={errorProduct}
            key={errorProduct.stacklineSku}
            onRemove={onRemove}
            disabled={disabled}
            isActionable={
              isActionable &&
              errorProduct?.userApprovalStatus === UserApprovalStatus.PENDING
            }
          />
        );
      })}
    </>
  );
};

const DeliverySection = () => {
  const { actorRef, send } = useCheckoutContext();

  const deliverySectionState = useSelector(
    actorRef,
    (state) => state.context.sectionState.delivery
  );

  const { open, chevronShow, errorMessage } = deliverySectionState;

  const toggleOpen = () => {
    if (open) {
      send({ type: "ON_SESSION_CLOSE", data: { sessionToClose: "delivery" } });
    } else {
      send({ type: "ON_SESSION_OPEN", data: { sessionToOpen: "delivery" } });
    }
  };

  const userRetailerCheckoutOrder = useSelector(
    actorRef,
    (snapshot) =>
      snapshot.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
  );

  const products = useSelector(
    actorRef,
    (snapshot) => snapshot.context.retailerCheckoutOrderStatus?.products
  );

  const userInteractionRequirement =
    userRetailerCheckoutOrder?.userInteractionRequirement;

  const shippingGroups =
    userRetailerCheckoutOrder?.shippingOptions?.shippingGroups;
  const [openOptions, setOpenOptions] = useState<boolean[]>([]);

  useEffect(() => {
    const length = shippingGroups?.length ?? 0;
    setOpenOptions(new Array(length).fill(open));
  }, [open, shippingGroups?.length]);

  const items = userRetailerCheckoutOrder?.items
    .map((item) => {
      const product = products?.[item.stacklineSku];
      if (product) {
        return {
          ...item,
          quantity: item.quantity * 1,
          title: product.title,
        };
      }
      return null;
    })
    .filter((item) => item !== null);

  const errorProducts = getErrorProducts(items ?? []);

  const [shippingGroupsClone, setShippingGroupsClone] = useState<
    ShippingGroup[]
  >(shippingGroups ?? []);

  // use a local clone of the shippingGroups and update the selected shipping option
  useEffect(() => {
    setShippingGroupsClone(shippingGroups ?? []);
  }, [shippingGroups]);

  const handleUpdateShippingOption = async (
    index: number,
    deliveryOption?: {
      name: string;
      cssSelector: string;
    },
    deliveryTimeWindow?: {
      name: string;
      cssSelector: string;
    }
  ) => {
    try {
      const updatedShippingGroups = shippingGroupsClone.map((e, i) => {
        if (index === i) {
          const deliveryOptions = e.deliveryOptions;
          const updatedDeliveryOptions = deliveryOptions.map((option) => {
            if (
              option.name === deliveryOption?.name &&
              option.cssSelector === deliveryOption?.cssSelector
            ) {
              if (deliveryTimeWindow) {
                option.deliveryTimeWindows = option.deliveryTimeWindows.map(
                  (window) => {
                    if (
                      window.name === deliveryTimeWindow?.name &&
                      window.cssSelector === deliveryTimeWindow?.cssSelector
                    ) {
                      return { ...window, isSelected: true };
                    }
                    return { ...window, isSelected: false };
                  }
                );
              }
              return { ...option, isSelected: true };
            }
            return { ...option, isSelected: false };
          });
          return { ...e, deliveryOptions: updatedDeliveryOptions };
        }
        return e;
      });
      setShippingGroupsClone(updatedShippingGroups);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveShippingOption = () => {
    send({
      type: "SUBMIT_USER_INTERACTION",
      data: {
        interactionType: UserInteractionType.SHIPPING_OPTION_SELECTION,
        interactionData: {
          shippingOption: {
            shippingGroups: shippingGroupsClone,
          },
        },
      },
    });
  };

  const checkoutStatus = userRetailerCheckoutOrder?.status;
  const checkoutStatusIsCompleted =
    checkoutStatus === UserRetailerCheckoutOrderStatusV2.checkout_success;
  const orderPlacedWithin30Days =
    userRetailerCheckoutOrder?.createdTime &&
    moment(userRetailerCheckoutOrder?.createdTime).isAfter(
      moment().subtract(30, "days")
    );

  const returnAvailableItems = items?.filter((item) => !item.returnTracking);

  const hasRetailerOrderId = items?.some((item) => item.retailerOrderId);

  const storefrontReturnsEnabled = useStorefrontReturnsEnabled(
    userRetailerCheckoutOrder?.retailerId
  );

  const returnsEnabled =
    userRetailerCheckoutOrder?.orderType === AsyncCheckoutOrderType.GUEST ||
    storefrontReturnsEnabled;

  return (
    <Box className="section">
      <div className="section_head" style={{ cursor: "pointer" }}>
        <div className="title">Delivery</div>
        {chevronShow ? (
          <ChevronDownIcon
            style={{
              transition: "transform 0.3s",
              width: "33px",
              height: "33px",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
            onClick={toggleOpen}
          ></ChevronDownIcon>
        ) : null}
      </div>
      <div>
        <StoreErrorV2
          errorMessage={errorMessage}
          sx={{
            width: "fit-content",
            marginTop: "2.2px",
            marginBottom: "24px",
            [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
              marginTop: "0px",
              marginBottom: "15px",
            },
          }}
        />
        <div className="shipping_group_list">
          {errorProducts.length > 0 ? (
            <ErrorProducts errorProducts={errorProducts} />
          ) : null}
          {shippingGroupsClone?.map((e, index) => {
            const productSKUs = e.products.map(
              (product) => product.retailerSku
            );
            // check if the retailerSku have retailerOrderId in items array
            const itemWithRetailerOrderId = items?.find(
              (item) =>
                productSKUs.includes(item.retailerSku) && item?.retailerOrderId
            );
            const retailerOrderId = itemWithRetailerOrderId?.retailerOrderId;
            const retailerOrderPageUrl = itemWithRetailerOrderId
              ? products?.[itemWithRetailerOrderId?.stacklineSku]
                  ?.retailerOrderPageUrl
              : undefined;
            return (
              <ShippingGroupPreview
                key={index}
                items={items}
                shippingGroup={e}
                index={index}
                handleUpdateShippingOption={(
                  deliveryOption,
                  deliveryTimeWindow
                ) =>
                  handleUpdateShippingOption(
                    index,
                    deliveryOption,
                    deliveryTimeWindow
                  )
                }
                deliveryOpen={openOptions}
                openItem={(index: number) => {
                  const newOpenOptions = [...openOptions];
                  newOpenOptions[index] = !newOpenOptions[index];
                  setOpenOptions(newOpenOptions);
                }}
                retailerOrderId={retailerOrderId}
                retailerOrderPageUrl={retailerOrderPageUrl}
                orderType={userRetailerCheckoutOrder?.orderType}
              />
            );
          })}
          {!shippingGroups ? (
            <>
              {items
                ?.filter((item) => item.status !== "error")
                ?.map((item) => {
                  return (
                    <SummaryProduct
                      product={item}
                      key={item.stacklineSku}
                      quantityToUse={
                        items?.find((e) => e.stacklineSku === item.stacklineSku)
                          ?.quantity
                      }
                    />
                  );
                })}
            </>
          ) : null}
          {userInteractionRequirement?.type ===
          UserInteractionType.SHIPPING_OPTION_SELECTION ? (
            <ButtonContainer
              sx={{
                alignSelf: "flex-end",
                justifyContent: "flex-end",
              }}
            >
              {/* <ClearButton>Clear</ClearButton> */}
              <SaveButton onClick={handleSaveShippingOption}>Save</SaveButton>
            </ButtonContainer>
          ) : null}
        </div>
      </div>

      {checkoutStatusIsCompleted &&
      orderPlacedWithin30Days &&
      returnsEnabled &&
      hasRetailerOrderId &&
      returnAvailableItems?.length ? (
        <div>
          <div
            style={{
              marginTop: "16px",
              textDecoration: "none",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              color: "inherit",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: 600,
              width: "fit-content",
            }}
            onClick={() => {
              ProductReturnDialog.show({
                orderId: userRetailerCheckoutOrder?.orderId,
                products: returnAvailableItems ?? [],
                useInitiateReturn: storefrontReturnsEnabled,
                updateOrder: (order) => {
                  send({
                    type: "UPDATE_RETAILER_CHECKOUT_ORDER",
                    data: {
                      retailerCheckoutOrder: {
                        ...userRetailerCheckoutOrder,
                        items: order.items,
                      },
                    },
                  });
                },
              });
            }}
          >
            <div>Return {pluralize("item", items?.length)}</div>
            <KeyboardArrowRightRounded
              style={{
                color: "inherit",
                fontSize: 21,
                marginLeft: 4,
                marginRight: -3,
              }}
            />
          </div>
        </div>
      ) : null}
    </Box>
  );
};

export default DeliverySection;
